import { useMemo } from 'react';
import { useWorkflowAccess } from '../utils/workflowAccess';
import { useWorkflowMetadata, WorkflowStatus } from './useWorkflowMetadata';

/**
 * Required fields information for a status transition
 */
export interface StatusConfiguration {
  /** Whether a reason must be selected */
  require_reason: boolean;
  /** Whether a note must be provided */
  require_note: boolean;
  /** Available reason options (if reason is required) */
  reasons: Array<{
    code: string;
    title: string;
    require_note: boolean;
  }>;
}

/**
 * Validation result for a status transition
 */
export interface TransitionValidation {
  /** Whether the transition is valid */
  isValid: boolean;
  /** Array of validation error messages */
  errors: string[];
  /** Information about required fields */
  statusConfiguration: StatusConfiguration;
}

/**
 * Data provided for a status transition
 */
export interface TransitionData {
  /** Reason code (if applicable) */
  reason?: string;
  /** Additional note/comment */
  note?: string;
}

export interface UseStatusTransitionsParams {
  /** Current status code of the object */
  currentStatus: string | null | undefined;
  /** Workflow scope identifier (e.g., 'assignments.opportunity', 'staffing.need') */
  workflowScope: string;
}

export function useStatusTransitions({ currentStatus, workflowScope }: UseStatusTransitionsParams) {
  const { statuses, isLoading, error } = useWorkflowMetadata(workflowScope);
  const { renderableStatusesFrom, renderableReasonsFor } = useWorkflowAccess(statuses);

  /**
   * Metadata for the current status
   */
  const currentStatusData = useMemo(
    () => (currentStatus ? statuses[currentStatus] : null),
    [statuses, currentStatus]
  );

  /**
   * Array of status objects that can be transitioned to from the current status.
   * Returns full WorkflowStatus objects, not just codes.
   */
  const availableTransitions = useMemo<WorkflowStatus[]>(() => {
    return renderableStatusesFrom(currentStatus);
  }, [renderableStatusesFrom, currentStatus]);

  /**
   * Check if a specific status transition is allowed from the current status.
   *
   * @param targetStatus - The status code to check
   * @returns true if transition is allowed, false otherwise
   *
   * @example
   * ```typescript
   * if (canTransitionTo('CLOSED')) {
   *   // Show close button
   * }
   * ```
   */
  const canTransitionTo = (targetStatus: string): boolean => {
    return currentStatusData?.allowed_transitions?.includes(targetStatus) || false;
  };

  const getStatusConfiguration = (targetStatus: string): StatusConfiguration => {
    const targetStatusData = statuses[targetStatus];

    if (!targetStatusData) {
      return {
        require_reason: false,
        require_note: false,
        reasons: [],
      };
    }

    return {
      require_reason: targetStatusData.require_reason || false,
      require_note: targetStatusData.require_note || false,
      reasons: renderableReasonsFor(targetStatus) || [],
    };
  };

  const validateTransition = (targetStatus: string, data: TransitionData): TransitionValidation => {
    const errors: string[] = [];
    const statusConfiguration = getStatusConfiguration(targetStatus);

    // Check if transition is allowed
    if (!canTransitionTo(targetStatus)) {
      errors.push(`Cannot transition from ${currentStatus || 'current state'} to ${targetStatus}`);
    }

    // Check if reason is required but not provided
    if (statusConfiguration.require_reason && !data.reason) {
      errors.push('Reason is required for this status change');
    }

    // Check if note is required but not provided
    if (statusConfiguration.require_note && !data.note?.trim()) {
      errors.push('Note is required for this status change');
    }

    // Check if specific reason requires a note
    if (data.reason && statusConfiguration.reasons.length > 0) {
      const reasonData = statusConfiguration.reasons.find((r) => r.code === data.reason);
      if (reasonData?.require_note && !data.note?.trim()) {
        errors.push(`A note is required when selecting "${reasonData.title}"`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      statusConfiguration,
    };
  };

  return {
    /** All workflow statuses (keyed by status code) */
    statuses,
    /** Metadata for the current status */
    currentStatusData,
    /** Array of statuses that can be transitioned to */
    availableTransitions,
    /** Audience-filtered reasons for a given status code */
    renderableReasonsFor,
    /** Check if a specific transition is allowed */
    canTransitionTo,
    /** Get required fields for a status transition */
    getStatusConfiguration,
    /** Validate a transition with provided data */
    validateTransition,
    /** Whether workflow metadata is loading */
    isLoading,
    /** Error from fetching workflow metadata */
    error,
  };
}
